import type {ImageField, KeyTextField, RichTextField} from '@prismicio/client'
import {isFilled} from '@prismicio/client'
import {PrismicNextImage} from '@prismicio/next/pages'
import {PrismicRichText} from '@prismicio/react'
import cn from 'classnames'
import {Gradient} from 'src/common/Gradient'
import {Icon} from 'src/common/Icon'
import type {BackgroundColors} from 'src/common/types'

type LogoBarV2Props = {
  logos: ImageField[]
  title?: KeyTextField
  disableFade?: boolean
  titleRte?: RichTextField
  isDarkMode?: boolean
  gradientColor?: BackgroundColors
  disableTopMargin?: boolean
}

/**
 * Component for "LogoBar" Slices.
 */
export const LogoBarV2 = ({
  logos,
  title,
  titleRte,
  disableFade = false,
  isDarkMode = true,
  gradientColor = 'Dark Green',
  disableTopMargin = false,
}: LogoBarV2Props): JSX.Element => {
  const textColor = isDarkMode ? 'text-moss-white' : 'text-moss-black'
  const cornerColor = isDarkMode ? 'text-moss-white/[70%]' : 'text-moss-black/[70%]'
  const logoFilter = isDarkMode ? 'brightness-0 invert' : 'brightness-0'

  return (
    <div
      className={cn(
        'relative flex h-auto w-full max-w-full scroll-mt-16 flex-col gap-6 md:h-[64px] md:flex-row md:items-center md:gap-[18px] md:pl-5',
        !disableTopMargin && 'md:mt-6',
      )}
    >
      {isFilled.keyText(title) && (
        <div className="flex items-center justify-center max-md:h-[38px]">
          <h3
            className={cn(
              'text-detail text-center uppercase md:min-w-[394px] md:text-start md:text-[12px] md:font-semibold md:leading-4 md:tracking-[.72px]',
              textColor,
            )}
          >
            {title}
          </h3>
        </div>
      )}
      {isFilled.richText(titleRte) && (
        <div className="flex items-center justify-center max-md:h-[38px]">
          <PrismicRichText
            field={titleRte}
            components={{
              paragraph: ({children}) => (
                <p
                  className={cn(
                    'text-detail text-center uppercase md:min-w-[394px] md:text-start md:text-[12px] md:font-semibold md:leading-4 md:tracking-[.72px]',
                    textColor,
                  )}
                >
                  {children}
                </p>
              ),
              strong: ({children}) => (
                <strong className={isDarkMode ? 'text-green-neon' : 'text-moss-green-500'}>{children}</strong>
              ),
            }}
          />
        </div>
      )}
      <div className="group relative flex h-[43px] w-full max-w-full overflow-hidden">
        {!disableFade && <Gradient fromColor={gradientColor} side="left" />}
        <div className="absolute left-0 top-0 flex h-[43px] w-full select-none overflow-hidden">
          <div className="group-hover:pause-animation flex w-max shrink-0 animate-[slideInfinite_18s_linear_infinite] items-center">
            {logos.map((logo, i) => (
              <PrismicNextImage
                key={'0-' + i}
                field={logo}
                className={cn('h-[48px] max-h-[48px] w-[144px] max-w-[144px]', logoFilter)}
              />
            ))}
          </div>
          <div className="group-hover:pause-animation flex w-max shrink-0 animate-[slideInfinite_18s_linear_infinite] items-center">
            {logos.map((logo, i) => (
              <PrismicNextImage
                key={'1-' + i}
                field={logo}
                className={cn('h-[48px] max-h-[48px] w-[144px] max-w-[144px]', logoFilter)}
              />
            ))}
          </div>
        </div>
        {!disableFade && <Gradient fromColor={gradientColor} side="right" />}
      </div>
      <Icon name="corner" className={cn('absolute left-0 top-0 h-[5px] w-[5px]', cornerColor)} />
      <Icon name="corner" className={cn('absolute right-0 top-0 h-[5px] w-[5px] scale-x-[-1]', cornerColor)} />
      <Icon
        name="corner"
        className={cn('absolute right-0 h-[5px] w-[5px] rotate-180 max-md:top-[33px] md:bottom-0', cornerColor)}
      />
      <Icon
        name="corner"
        className={cn('absolute left-0 h-[5px] w-[5px] scale-y-[-1] max-md:top-[33px] md:bottom-0', cornerColor)}
      />
    </div>
  )
}
